import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import {
  SHIPPING,
  SIZE_OPTIONS_BY_CATEGORY,
  TRAINING_SUIT_FEMALE_SIZES,
  TRAINING_SUIT_MALE_SIZES,
} from './enums';

export const bytesToMB = bytes => {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};

export function isFunction(value) {
  return typeof value === 'function';
}

export const useStateRef = initialState => {
  const [state, setState] = useState(initialState);
  const ref = useRef(initialState);

  const dispatch = useCallback(stateToSet => {
    ref.current = isFunction(stateToSet) ? stateToSet(ref.current) : stateToSet;
    setState(ref.current);
  }, []);

  return [state, dispatch, ref];
};
export const shuffleArray = array => {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};
export function retrieveUserFavorites(user) {
  if (!user || !user.id) {
    return null;
  }
  const protectedData = user.attributes?.profile?.protectedData || {};
  return protectedData.favorites ?? [];
}
export const renameObjKeyToValue = arr => {
  return arr.map(item => {
    const { key, ...rest } = item;
    return { value: key, ...rest };
  });
};

export const capitalizeFirstLetter = string => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const toLowerCase = str => {
  if (!str) return '';
  return str.toString().toLowerCase();
};
export const getKeyFromObject = (obj, keyFragment) => {
  // Convert the key fragment to lowercase for case-insensitive search
  const lowerCaseKeyFragment = keyFragment.toLowerCase();

  // Iterate over the keys of the object
  for (let key in obj) {
    if (key.toLowerCase().includes(lowerCaseKeyFragment)) {
      const value = obj[key];
      // Check if the value is an array, and return the first item if it is
      if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : null;
      }
      // Return the value if it is not an array
      return value;
    }
  }

  // Return null if no key is found
  return '';
};

export const formatNumber = number => {
  if (!number) return null;
  if (number >= 1000000) {
    return (number / 1000000).toFixed(1) + 'M';
  } else if (number >= 1000) {
    return (number / 1000).toFixed(1) + 'k';
  }
  return number.toString();
};

const generateRandomString = length => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const generateUniqueDisplayName = listingTitle => {
  const randomString = generateRandomString(5); // Adjust the length as needed
  return `${listingTitle}${randomString}`;
};
export const normalizedStatus = status => status?.trim().toLowerCase();

// Helper function to sort options alphabetically using Intl.Collator and a specified key
export const sortOptionsByKeyIntl = (options, key) => {
  const collator = new Intl.Collator('en', { sensitivity: 'base' });

  const uniqueOptions = Array.from(new Set(options.map(option => option[key]?.trim()))).map(value =>
    options.find(option => option[key]?.trim() === value)
  );

  return uniqueOptions.sort((a, b) => collator.compare(a[key]?.trim(), b[key]?.trim()));
};

export const extractStripePaymentMethodId = currentUser => {
  if (!currentUser || !currentUser.attributes || !currentUser.attributes.defaultPaymentMethod) {
    return null;
  }

  const { defaultPaymentMethod } = currentUser.attributes;

  if (defaultPaymentMethod.attributes && defaultPaymentMethod.attributes.stripePaymentMethodId) {
    return defaultPaymentMethod.attributes.stripePaymentMethodId;
  }

  return null;
};

/**
 * Filters an array of objects to exclude those with a `userType` in the `metadata`.
 * @param {Array} dataArray - The array of objects to filter.
 * @returns {Array} - Updated array with objects that don't have a `userType` in `metadata`.
 */
export function filterWithoutUserType(dataArray) {
  return dataArray.filter(item => !item?.product?.metadata?.userType);
}
export const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

export const formatStringtoCamelCase = str => {
  return str
    .split(' ') // Split the string into words
    .map(
      (word, index) =>
        index === 0
          ? word.toLowerCase() // Lowercase the first word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize the first letter of subsequent words
    )
    .join(''); // Join them back together
};

// Helper function to calculate age
export const calculateAge = dateOfBirth => {
  const dob = moment(dateOfBirth, 'MM/DD/YYYY'); // Adjust format if needed
  const currentDate = moment();
  return currentDate.diff(dob, 'years'); // Calculate difference in years
};

export const decryptNumber = (encryptedNumber, key) => {
  if (!encryptedNumber || !key) return null;
  const decrypted = encryptedNumber ^ key;
  return decrypted;
};

export const convertEurToUsd = (priceInEur, conversionRate = 1.1) => {
  return (priceInEur * conversionRate).toFixed(2);
};

/**
 * 1) Define constants for all Stripe price IDs
 */
export const PRICE_ID_TV_INFLUENCER = 'price_1R1XdSRpgIODOgxqoIERpaEG';
export const PRICE_ID_SPECIALIST = 'price_1R1Xd1RpgIODOgxqnxg0IKsK';
export const PRICE_ID_LUMINARY = 'price_1R1XdGRpgIODOgxqhp9nXeg8';

/**
 * 2) Create a mapping object from each price ID to plan details.
 */
export const priceMapping = {
  [PRICE_ID_TV_INFLUENCER]: {
    name: 'TV Influencer',
    tagline: 'For the Internationally-Recognized Spiritual Expert',
    benefits: [
      'All the benefits of Specialist and more!',
      'Create & Host Your Own TV Series of 8-12 episodes',
      'Reach millions through Roku, Amazon, Apple TV, and Android TV platforms on your TV, as well as online and through the SoulSearch TV mobile apps for Android and iPhone',
      'Exclusive marketing and promotional support in the SoulSearch newsletters, on SoulSearch online programming, and at the live in-person SoulSearch events',
    ],
    buttonText: 'Broadcast Your Vision',
    hasPrice: false,
  },
  [PRICE_ID_SPECIALIST]: {
    name: 'Specialist',
    tagline: 'For the Emerging Spiritual Entrepreneur',
    benefits: [
      'Showcase your expertise by creating a comprehensive SoulSearch profile (single listing in your chosen category)',
      'Use your profile as your website if you are a new entrepreneur (save on hosting fees!)',
      'Increase your visibility to a global audience of seekers',
      'Access to our SoulSearch Spiritual & Holistic Wellness Facebook Group and Community',
      'Engage in discussions, share experiences, and ask questions in a nurturing space',
    ],
    buttonText: 'Unlock Your Potential',
    hasPrice: true,
  },
  [PRICE_ID_LUMINARY]: {
    name: 'Luminary',
    tagline: 'For the Established Spiritual Leader',
    benefits: [
      'All the benefits of Specialist and more!',
      'Content Sharing: Post articles, videos, and audio files on the SoulSearch platform and share them in our community',
      'Customer Reviews: Build your reputation with customer reviews',
      'Profile Links: Live links from your profile to your website and socials',
      'Monthly VIP Training on tools for amplifying your visibility',
    ],
    buttonText: 'Illuminate Your Path',
    hasPrice: true,
  },
};

/**
 * 3) (Optional) Helper function to get plan details by price ID
 */
export function getPlanInfoByPriceId(priceId) {
  return priceMapping[priceId] || undefined;
}

export const getUserTpe = currentUser => {
  if (!currentUser || !currentUser.id) {
    return null;
  }

  const { userType } = currentUser.attributes?.profile?.publicData || {};
  return userType;
};

export const transformListingDataForDraft = data => {
  // Create a deep clone of the object to avoid mutating the original data
  const cleanedData = JSON.parse(JSON.stringify(data));

  // Recursively remove null or undefined values
  function removeNullOrUndefined(obj) {
    Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        removeNullOrUndefined(obj[key]);
      } else if (obj[key] === null || obj[key] === undefined) {
        delete obj[key];
      }
    });
  }

  // Clean the main object from null and undefined values
  removeNullOrUndefined(cleanedData);

  // Transform `images` to `images`, keeping only `id` in each image object
  if (Array.isArray(cleanedData.images)) {
    cleanedData.images = cleanedData.images.map(photo => {
      const { id } = photo; // Keep only `id`
      return { id };
    });
    delete cleanedData.images;
  }

  // Destructure top-level attributes, leaving others for publicData
  const {
    title,
    description,
    createdAt,
    price,
    id,
    geolocation,
    availabilityPlan,
    images,
    shippingPriceInSubunitsAdditionalItems,
    shippingPriceInSubunitsOneItem,
    ...otherData
  } = cleanedData || {};

  // Determine how to handle the `price` field
  let finalPrice = price;
  if (shippingPriceInSubunitsAdditionalItems || shippingPriceInSubunitsOneItem) {
    finalPrice = price ? price.amount : null;
  }

  // Construct the transformed object with the specified hierarchy
  const transformedData = {
    title,
    description,
    price,
    id,
    images,
    publicData: {
      geolocation,
      availabilityPlan,

      ...(shippingPriceInSubunitsAdditionalItems?.amount && {
        shippingPriceInSubunitsAdditionalItems: shippingPriceInSubunitsAdditionalItems?.amount,
      }),
      ...(shippingPriceInSubunitsOneItem?.amount && {
        shippingPriceInSubunitsOneItem: shippingPriceInSubunitsOneItem?.amount,
      }),
      ...otherData, // Any remaining data goes into publicData
    },
  };

  // Final clean to ensure there are no null or undefined values left
  removeNullOrUndefined(transformedData);

  return transformedData;
};

export const formatListingData = values => {
  const {
    images,
    title,
    price,
    shippingPriceInSubunitsAdditionalItems,
    shippingPriceInSubunitsOneItem,
    deliveryOptions,
    stock,
    description,
    ...rest
  } = values || {};

  const shippingDataMaybe = {
    shippingPriceInSubunitsOneItem: shippingPriceInSubunitsOneItem?.amount,
    shippingPriceInSubunitsAdditionalItems: shippingPriceInSubunitsAdditionalItems?.amount,
  };

  const shippingEnabled = Array.isArray(deliveryOptions) && deliveryOptions.includes(SHIPPING);

  let updatedValues = {
    title: title || null, // Keep title intact
    description: description || '', // Add description at the root level
    price: price || null,
    stockUpdate: {
      oldTotal: null, // Assuming no current stock info is provided
      newTotal: stock,
    },
    publicData: {
      ...rest,
    },
  };

  if (shippingEnabled) {
    Object.assign(updatedValues.publicData, {
      ...shippingDataMaybe,
      shippingEnabled,
      deliveryOptions,
    });
  }

  // Remove null or undefined values from updatedValues
  const cleanData = obj =>
    Object.entries(obj)
      .filter(([_, v]) => v != null) // Remove keys with null or undefined values
      .reduce(
        (acc, [k, v]) => ({
          ...acc,
          [k]: typeof v === 'object' && !Array.isArray(v) ? cleanData(v) : v,
        }),
        {}
      );

  updatedValues = cleanData(updatedValues);

  // Exclude images from the final result
  delete updatedValues.images;
  delete updatedValues.stockUpdate;

  return updatedValues;
};

export const getListingImage = images => {
  if (!images?.length) return [];

  return (
    images.map(image => ({
      fileLink:
        image.attributes?.variants?.['listing-card']?.url ||
        image.attributes?.variants?.['listing-card-2x']?.url,
      id: image.id,
    })) || []
  );
};
// Helper to strip the extension from a file name
export const getBaseName = (fullName = '') => {
  // If there's no dot, return the full name. Otherwise, remove everything from the last dot onward.
  return fullName.replace(/\.[^/.]+$/, '');
};
export const extractFileExtension = filename => {
  // Check if file is valid and has a 'name' property
  if (!filename) {
    return null; // Return null if file or file.name is not defined
  }

  // Extract and return the file extension
  return filename.substring(filename.lastIndexOf('.') + 1).toLowerCase();
};

export const getSizeOptionsForCategory = category => {
  return SIZE_OPTIONS_BY_CATEGORY[category] || [];
};

function toCamelCaseLabel(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .split(/[_\s]+/)
    .map(function(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

const toTitleCase = str =>
  String(str || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

const normalizeToLabel = val => {
  if (val == null) return val;
  if (typeof val === 'string') return toTitleCase(val);
  return val;
};

export function getPublicDataLabels(publicData = {}, config = {}) {
  const result = {};

  const listingFields = config?.listing?.listingFields || [];
  const fieldByKey = listingFields.reduce((acc, f) => {
    acc[f.key] = f;
    return acc;
  }, {});

  const getEnumLabel = (field, rawVal) => {
    if (!field || rawVal == null) return normalizeToLabel(rawVal);
    const match = field.enumOptions?.find(opt => opt.option === rawVal);
    return match?.label || normalizeToLabel(rawVal);
  };

  // ---- Category mapping (supports category1/category2 and categoryLevel1/categoryLevel2) ----
  const categories = config?.categoryConfiguration?.categories || [];
  const cat1Id = publicData?.categoryLevel1 || publicData?.category1;
  const cat2Id = publicData?.categoryLevel2 || publicData?.category2;

  if (cat1Id) {
    const cat1 = categories.find(c => c.id === cat1Id);
    result.category = cat1?.name || normalizeToLabel(cat1Id);

    if (cat2Id) {
      const cat2 =
        cat1?.subcategories?.find(sc => sc.id === cat2Id) ||
        categories.flatMap(c => c.subcategories || []).find(sc => sc.id === cat2Id);

      result.subCategory = cat2?.name || normalizeToLabel(cat2Id);
    }
  }

  // ---- Listing fields mapping (brand/color/condition/size etc) ----
  // If a field exists in config and we have a value in publicData, map to label.
  listingFields.forEach(field => {
    const key = field.key;
    const rawValue = publicData?.[key];

    if (rawValue == null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0)) {
      return;
    }

    if (Array.isArray(rawValue)) {
      result[key] = rawValue.map(v => getEnumLabel(field, v)).filter(Boolean);
    } else {
      result[key] = getEnumLabel(field, rawValue);
    }
  });

  // ---- Fallback: include other publicData keys not in config (e.g. gender) ----
  Object.keys(publicData || {}).forEach(key => {
    // skip category keys + fields we already mapped above
    if (
      key === 'categoryLevel1' ||
      key === 'categoryLevel2' ||
      key === 'category1' ||
      key === 'category2' ||
      key === 'category' ||
      fieldByKey[key]
    ) {
      return;
    }

    const rawValue = publicData[key];
    if (rawValue == null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0)) {
      return;
    }

    result[key] = Array.isArray(rawValue)
      ? rawValue.map(normalizeToLabel)
      : normalizeToLabel(rawValue);
  });

  return result;
}

export const getCartItems = user => {
  return user?.attributes?.profile?.publicData?.cartItems || [];
};
export const getFavorites = user => {
  return user?.attributes?.profile?.publicData?.favorites || [];
};

export const groupListingsByAuthor = listings => {
  return listings.reduce((grouped, listing) => {
    const authorId = listing?.author?.id?.uuid;
    if (!grouped[authorId]) {
      grouped[authorId] = [];
    }
    grouped[authorId].push(listing);
    return grouped;
  }, {});
};




export const getTrainingSuitSizesForGender = gender => {
  if (gender === 'female') return TRAINING_SUIT_FEMALE_SIZES;
  if (gender === 'male') return TRAINING_SUIT_MALE_SIZES;

  // gender "other": show union of both sets (unique)
  const union = [...TRAINING_SUIT_FEMALE_SIZES, ...TRAINING_SUIT_MALE_SIZES];
  return Array.from(new Set(union));
};

// Put this in same file (above component) or in a util file.

const isNil = v => v === null || v === undefined;

const SIZE_ALPHA_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '1X',
  '2X',
  '3X',
  '4X',
  '3XL', // some catalogs use 3XL instead of 3X
];

const SIZE_WORD_ORDER = ['Small', 'Medium', 'Large'];

const AGE_ORDER = ['Junior', 'Adult'];

// Extract a number if the whole string is a number, e.g. "4", "16", "44"
const parsePureNumber = str => {
  const s = String(str || '').trim();
  return /^\d+(\.\d+)?$/.test(s) ? Number(s) : null;
};

// Youth pattern: "YXXS (6)", "YM (10)" => capture the number in parentheses
const parseYouthParenNumber = str => {
  const s = String(str || '').trim();
  const m = s.match(/\((\d+)\)/);
  return m ? Number(m[1]) : null;
};

const getSizeSortKey = raw => {
  const s = String(raw || '').trim();

  // 1) Pure numeric sizes first (ascending)
  const pureNum = parsePureNumber(s);
  if (!isNil(pureNum)) return { group: 0, index: pureNum, tiebreak: s };

  // 2) Alpha sizes (XXS.. etc)
  const alphaIdx = SIZE_ALPHA_ORDER.indexOf(s);
  if (alphaIdx !== -1) return { group: 1, index: alphaIdx, tiebreak: s };

  // 3) Youth sizes (with numeric in parentheses) e.g. "YXS (7)"
  // Sort by the number in parentheses
  const youthNum = parseYouthParenNumber(s);
  if (!isNil(youthNum) && /^Y/i.test(s)) return { group: 2, index: youthNum, tiebreak: s };

  // 4) Junior/Adult
  const ageIdx = AGE_ORDER.indexOf(s);
  if (ageIdx !== -1) return { group: 3, index: ageIdx, tiebreak: s };

  // 5) Word sizes Small/Medium/Large
  const wordIdx = SIZE_WORD_ORDER.indexOf(s);
  if (wordIdx !== -1) return { group: 4, index: wordIdx, tiebreak: s };

  // 6) "Other" always last
  if (s.toLowerCase() === 'other') return { group: 99, index: 0, tiebreak: s };

  // 7) Unknowns near the end, alphabetical
  return { group: 50, index: 0, tiebreak: s.toLowerCase() };
};

export const sortOptionsForFilter = (name, options = []) => {
  if (name !== 'size') return options; // keep everything else as-is
  if (!Array.isArray(options)) return options;

  // stable-ish copy
  return [...options].sort((a, b) => {
    const av = a?.option ?? a?.label ?? '';
    const bv = b?.option ?? b?.label ?? '';

    const ka = getSizeSortKey(av);
    const kb = getSizeSortKey(bv);

    if (ka.group !== kb.group) return ka.group - kb.group;
    if (ka.index !== kb.index) return ka.index - kb.index;

    // final tie-break: label text
    const al = String(a?.label ?? av).toLowerCase();
    const bl = String(b?.label ?? bv).toLowerCase();
    return al.localeCompare(bl);
  });
};
